// mui
import { Box, Button, Card, Chip, Container, Stack, Typography } from '@mui/material';

// components
import Page from '../components/Page';
import Iconify from '../components/Iconify';
import ProductsConfig from '../sections/downloads/productsConfig';
import { useLanguage } from '../context/LanguageContext';

// ----------------------------------------------------------------------

const platformConfig = [
  { key: 'web', label: 'Web app', icon: 'solar:global-bold-duotone', variant: 'contained' },
  { key: 'chrome', label: 'Chrome', icon: 'logos:chrome', variant: 'outlined' },
  { key: 'edge', label: 'Edge', icon: 'logos:microsoft-edge', variant: 'outlined' },
  { key: 'opera', label: 'Opera', icon: 'simple-icons:opera', variant: 'outlined' },
  { key: 'android', label: 'Android', icon: 'logos:google-play-icon', variant: 'outlined' },
];

function getPlatforms(product) {
  return platformConfig.filter((platform) => product[platform.key]);
}

export default function Downloads() {
  const { t } = useLanguage();
  const downloads = t('downloads'); // Downloads
  const featuredProduct = ProductsConfig[0];
  const otherProducts = ProductsConfig.slice(1);

  return (
    <Page title={downloads}>
      <Container maxWidth="lg">
        <Stack spacing={3}>
          <Card
            sx={{
              p: { xs: 3, md: 4 },
              overflow: 'hidden',
              position: 'relative',
              color: 'common.white',
              background: 'linear-gradient(135deg, #171717 0%, #23221F 54%, #0F3D28 100%)',
            }}
          >
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={3} alignItems={{ md: 'center' }} justifyContent="space-between">
              <Box sx={{ maxWidth: 650 }}>
                <Chip label="Downloads" color="secondary" sx={{ mb: 2, fontWeight: 800 }} />
                <Typography variant="h3" sx={{ color: 'common.white' }}>
                  Add Voice Typing where you work.
                </Typography>
                <Typography sx={{ color: 'rgba(255,255,255,0.72)', mt: 1.5, lineHeight: 1.8 }}>
                  Install the browser extension, open the web app, or discover companion tools for OCR, translation,
                  dictionary lookup, quizzes, and AI commands.
                </Typography>
              </Box>

              <Box
                sx={{
                  width: { xs: '100%', md: 340 },
                  p: 2,
                  borderRadius: 2,
                  bgcolor: 'rgba(255,255,255,0.08)',
                  border: '1px solid rgba(255,255,255,0.16)',
                }}
              >
                <Stack direction="row" spacing={2} alignItems="center">
                  <Box
                    component="img"
                    src={featuredProduct.img}
                    alt={featuredProduct.title}
                    sx={{ width: 72, height: 72, borderRadius: 2, objectFit: 'cover', bgcolor: 'common.white' }}
                  />
                  <Box>
                    <Typography variant="h6" sx={{ color: 'common.white' }}>
                      {featuredProduct.title}
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.68)', mt: 0.5 }}>
                      Recommended for browser dictation.
                    </Typography>
                  </Box>
                </Stack>
                <Stack direction="row" spacing={1} sx={{ mt: 2, flexWrap: 'wrap', rowGap: 1 }}>
                  {getPlatforms(featuredProduct).map((platform) => (
                    <Button
                      key={platform.key}
                      href={featuredProduct[platform.key]}
                      target="_blank"
                      rel="noreferrer"
                      size="small"
                      variant={platform.key === 'chrome' ? 'contained' : 'outlined'}
                      startIcon={<Iconify icon={platform.icon} width={18} height={18} />}
                      sx={{
                        color: platform.key === 'chrome' ? 'primary.main' : 'common.white',
                        bgcolor: platform.key === 'chrome' ? 'common.white' : 'transparent',
                        borderColor: 'rgba(255,255,255,0.32)',
                        '&:hover': {
                          bgcolor: platform.key === 'chrome' ? 'rgba(255,255,255,0.9)' : 'rgba(255,255,255,0.08)',
                          borderColor: 'common.white',
                        },
                      }}
                    >
                      {platform.label}
                    </Button>
                  ))}
                </Stack>
              </Box>
            </Stack>
          </Card>

          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))', lg: 'repeat(3, minmax(0, 1fr))' },
              gap: 2.5,
            }}
          >
            {[featuredProduct, ...otherProducts].map((product) => (
              <ProductCard key={product.title} product={product} />
            ))}
          </Box>
        </Stack>
      </Container>
    </Page>
  );
}

function ProductCard({ product }) {
  const platforms = getPlatforms(product);
  const primaryPlatform = platforms.find((platform) => platform.key === 'chrome') || platforms[0];

  return (
    <Card
      sx={{
        p: 2.5,
        height: '100%',
        boxShadow: 0,
        border: (theme) => `1px solid ${theme.palette.divider}`,
      }}
    >
      <Stack spacing={2.5} sx={{ height: '100%' }}>
        <Stack direction="row" spacing={2} alignItems="center">
          <Box
            component="img"
            src={product.img}
            alt={product.title}
            sx={{
              width: 64,
              height: 64,
              borderRadius: 2,
              objectFit: 'cover',
              bgcolor: 'background.neutral',
              flexShrink: 0,
            }}
          />
          <Box sx={{ minWidth: 0 }}>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ flexWrap: 'wrap', rowGap: 0.5 }}>
              <Typography variant="h6">{product.title}</Typography>
              {product.isNew && <Chip label="New" size="small" color="info" variant="outlined" />}
            </Stack>
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              by {product.author}
            </Typography>
          </Box>
        </Stack>

        <Typography variant="body2" sx={{ color: 'text.secondary', lineHeight: 1.7, minHeight: 48 }}>
          {product.description}
        </Typography>

        <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', rowGap: 1 }}>
          {platforms.map((platform) => (
            <Chip
              key={platform.key}
              size="small"
              label={platform.label}
              icon={<Iconify icon={platform.icon} width={16} height={16} />}
              sx={{ bgcolor: 'background.neutral' }}
            />
          ))}
        </Stack>

        <Box sx={{ flexGrow: 1 }} />

        <Stack direction="row" spacing={1.25}>
          {primaryPlatform && (
            <Button
              fullWidth
              href={product[primaryPlatform.key]}
              target="_blank"
              rel="noreferrer"
              variant="contained"
              startIcon={<Iconify icon={primaryPlatform.icon} width={18} height={18} />}
            >
              Get it
            </Button>
          )}
          {product.web && (
            <Button
              href={product.web}
              target="_blank"
              rel="noreferrer"
              variant="outlined"
              sx={{ minWidth: 112 }}
              startIcon={<Iconify icon="solar:global-bold-duotone" width={18} height={18} />}
            >
              Website
            </Button>
          )}
        </Stack>
      </Stack>
    </Card>
  );
}
